'use client';

import { Suspense, useState, useEffect } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { getNews, getAppInfo, getNewsCategories } from "lib/getFetchers";
import { listToCategoryTree, flattenCategoryTree } from "lib/categoryTree";
import Breadcrumb from "components/Breadcrumb";
import NewsHero from "components/NewsHero";
import NewsGrid from "components/NewsGrid";
import AppSection from "components/AppSection";
import TaxonomyCategoryFilter from "components/TaxonomyCategoryFilter";
import CategoryListSkeleton from "components/CategoryListSkeleton";
import ux from "styles/pages/news-podcast-list.module.css";

function NewsPageContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const categoryId = (searchParams.get("categories") || "").trim();

  const [news, setNews] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [appInfo, setAppInfo] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);

  const newsQuery = (page) => {
    let q = `limit=20&sort=-createdAt&page=${page}`;
    if (categoryId) q += `&categories=${encodeURIComponent(categoryId)}`;
    return q;
  };

  useEffect(() => {
    loadAppInfo();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { categories = [], error } = await getNewsCategories(
        "limit=500&sort=position",
      );
      if (cancelled || error) return;
      const list = Array.isArray(categories) ? categories : [];
      const tree = listToCategoryTree(list);
      setCategoryOptions(flattenCategoryTree(tree));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setListLoading(true);
      setNews([]);
      setPagination(null);
      setCurrentPage(1);
      try {
        const {
          news: newsData = [],
          pagination: paginationData,
        } = await getNews(newsQuery(1));
        if (cancelled) return;
        setNews(newsData);
        setPagination(paginationData);
      } finally {
        if (!cancelled) setListLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [categoryId]);

  const loadAppInfo = async () => {
    const { appInfo: data } = await getAppInfo();
    setAppInfo(data);
  };

  const handleCategorySelect = (id) => {
    if (id) {
      router.replace(`/news?categories=${encodeURIComponent(id)}`, {
        scroll: false,
      });
    } else {
      router.replace("/news", { scroll: false });
    }
  };

  const loadMoreNews = async () => {
    if (loadingMore || !pagination) return;

    const nextPage = currentPage + 1;
    if (nextPage > pagination.pageCount) return;

    setLoadingMore(true);
    try {
      const {
        news: newsData = [],
        pagination: paginationData,
      } = await getNews(newsQuery(nextPage));
      setNews((prev) => [...prev, ...newsData]);
      setPagination(paginationData);
      setCurrentPage(nextPage);
    } catch (error) {
      console.error("Error loading more news:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  const breadcrumbItems = [
    { label: "Нүүр хуудас", url: "/" },
    { label: "Нийтлэл", url: null },
  ];

  const featuredNews = news.slice(0, 2);
  const gridNews = news.slice(2);

  const hasMore = pagination && currentPage < pagination.pageCount;

  const selectedCategoryName = categoryId
    ? categoryOptions.find((c) => String(c._id) === categoryId)?.name ||
      categoryOptions.find((c) => String(c._id) === categoryId)?.title ||
      ""
    : "";

  return (
    <div className="container py-4">
      <Breadcrumb items={breadcrumbItems} />
      <h1 style={{ fontSize: '1.4rem', marginBottom: '1rem', marginTop: '1rem', fontWeight: 600 }}>Нийтлэл</h1>

      <TaxonomyCategoryFilter
        options={categoryOptions}
        selectedId={categoryId}
        onSelect={handleCategorySelect}
        ariaLabel="Нийтлэл — ангиллаар шүүх"
      />

      {listLoading ? (
        <div aria-busy="true" aria-live="polite">
          <span className="visually-hidden">Жагсаалт ачааллаж байна</span>
          <span className={ux.loadingLabel}>Ачааллаж байна…</span>
          <CategoryListSkeleton variant="news" />
        </div>
      ) : (
        <div
          key={categoryId || "all"}
          className={ux.contentReveal}
        >
          {news.length > 0 ? (
            <div style={{ marginBottom: '2rem' }}>
              <NewsHero featured={featuredNews} />
            </div>
          ) : null}

          {news.length === 0 ? (
            <div className={ux.emptyWrap}>
              <p className={ux.emptyTitle}>
                {selectedCategoryName
                  ? `«${selectedCategoryName}» ангилалд нийтлэл алга`
                  : "Одоогоор нийтлэл байхгүй байна"}
              </p>
              <p className={ux.emptyHint}>
                Өөр ангилал сонгоод үзэх эсвэл бүх нийтлэл рүү буцаж очно уу.
              </p>
              {categoryId ? (
                <button
                  type="button"
                  className={ux.resetBtn}
                  onClick={() => handleCategorySelect("")}
                >
                  Бүх нийтлэл харах
                </button>
              ) : null}
            </div>
          ) : (
            <NewsGrid news={gridNews} />
          )}

          {hasMore ? (
            <div className={ux.loadMoreWrap}>
              <button
                type="button"
                onClick={loadMoreNews}
                disabled={loadingMore}
                className={ux.loadMoreBtn}
              >
                {loadingMore ? "Ачааллаж байна…" : "Цааш үзэх"}
              </button>
            </div>
          ) : null}
        </div>
      )}

      {appInfo && <AppSection appInfo={appInfo} />}
    </div>
  );
}

export default function NewsPage() {
  return (
    <Suspense
      fallback={
        <div className="container py-4" style={{ textAlign: "center" }}>
          Хуудас ачааллаж байна…
        </div>
      }
    >
      <NewsPageContent />
    </Suspense>
  );
}
